/**
 * What buying this length of this plan would actually do, worked out by the
 * server for this reader.
 *
 * The price alone is not the offer. Buying a larger plan mid-period credits
 * what is left of the current one, so the card says "30 days" and the buyer
 * gets 35 — without `bonusDays` and `periodEnd` on screen, that difference
 * reads as a mistake in the arithmetic rather than as change in their favour.
 */
export type PlanPrice = {
  durationDays: number
  amountCents: number
  /** What this purchase is, from the server's side of the till. */
  scenario: PurchaseScenario
  /** Days credited from the plan being replaced. Non-zero only on an upgrade. */
  bonusDays: number
  /** When the period this purchase buys would run out. */
  periodEnd: string
  /** A downgrade waits for the current plan; this is when it would begin. */
  startsAt?: string
}

export const PURCHASE_SCENARIOS = ['new', 'renew', 'upgrade', 'downgrade'] as const
export type PurchaseScenario = (typeof PURCHASE_SCENARIOS)[number]

export type Plan = {
  id: string
  slug: string
  nameRu: string
  nameEn: string
  tier: number
  /** null = unlimited. */
  maxCampaigns: number | null
  isPopular: boolean
  prices: PlanPrice[]
  /**
   * Not buyable by this reader. The card stays — a plan that vanishes looks
   * like the shop is broken, while a dimmed one with a reason explains itself.
   */
  isUnavailable: boolean
  /** Why, so the panel can write a sentence instead of only greying a button. */
  unavailableReason: string
}

/** Сети приёма USDT. Значения — те же, что понимает платёжный модуль. */
export const NETWORKS = ['tron', 'bsc'] as const
export type Network = (typeof NETWORKS)[number]

export type Invoice = {
  id: string
  amountCents: number
  network: Network
  symbol: string
  decimals: number
  payAddress: string
  /**
   * Сумма к оплате в наименьших единицах токена — строкой, и это не стиль.
   * У USDT в BSC 18 знаков: 20 USDT это 2e19, что больше Number.MAX_SAFE_INTEGER,
   * и число такого размера JavaScript округлит молча. По этой сумме модуль
   * опознаёт счёт, так что округлённая принадлежит уже не ему.
   */
  payAmountUnits: string
  status: string
  confirmations: number
  expiresAt: string
  confirmedAt: string
  createdAt: string
}

export type CreatedInvoice = {
  invoiceId: string
  amountCents: number
  network: Network
  symbol: string
  decimals: number
  payAddress: string
  payAmountUnits: string
  status: string
  expiresAt: string
}

/**
 * Periods are counted in days, not months, and the server is strict about it:
 * `POST /invoices` takes `duration_days` and rejects anything outside this set.
 * Keep the two in step — a month is not a fixed number of days, and the money
 * is charged for days.
 */
export const DURATION_DAYS = [7, 30, 90, 180] as const
export type DurationDays = (typeof DURATION_DAYS)[number]

/**
 * Покупка, названная на странице и ещё не превращённая в счёт.
 *
 * Цену несёт сама заявка, а не только id тарифа: карточка уже нашла её по
 * выбранному периоду, и второй поиск в модалке — это второй шанс разойтись с
 * тем, что человек прочитал перед кликом.
 */
export type PurchaseDraft = {
  plan: Plan
  price: PlanPrice
}
